/**
 * 金额展示工具（仅展示层）
 *
 * 规则：
 *   - 客户控制台：价格/报价按用户偏好货币（users.currency）折算显示（参考价，汇率 = /api/currencies，
 *     与后端 CurrencyConverter 同一张表）；实际扣款金额以付款时后端按快照算出的为准。
 *   - 账单、流水、余额、付款/退款金额等「记录」传 { original: true }，保持记录自己的币种不换算。
 *   - 管理后台、换算关闭、汇率缺失/过期 → 不换算。
 *   CNY/RMB → ¥，USD → $，其余常见币种用各自符号，未知币种直接显示 ISO 代码前缀（如 "SGD 12.00"）。
 *   记录没有币种时（例如优惠券这类币种无关的面额），不臆造符号，只显示数字。
 */

/**
 * 平台定价币种（2026-10-21 起全平台 USD）。仅用于后端没随价格返回币种的场景
 * （EVM 目录/弹性/附加项、VPC 对等等），有记录自己的币种时一律用记录的。
 */
export const PLATFORM_CURRENCY = 'USD';

const SYMBOLS = {
    CNY: '¥',
    RMB: '¥',
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: 'JP¥',
    HKD: 'HK$',
    TWD: 'NT$',
    SGD: 'S$',
    AUD: 'A$',
    CAD: 'C$',
    KRW: '₩',
    MYR: 'RM',
};

/** 归一化币种代码；兼容后端偶尔直接塞符号的情况（"¥"/"$"） */
export const normalizeCurrency = (currency) => {
    if (currency === null || currency === undefined) return '';
    const c = String(currency).trim();
    if (!c) return '';
    if (c === '¥' || c === '￥') return 'CNY';
    if (c === '$') return 'USD';
    return c.toUpperCase();
};

/**
 * 币种前缀：已知币种返回符号，未知币种返回 "CODE "，无币种返回 ''。
 */
export const currencySymbol = (currency) => {
    const code = normalizeCurrency(currency);
    if (!code) return '';
    return SYMBOLS[code] || `${code} `;
};

/**
 * 格式化金额。
 * @param {number|string} amount
 * @param {string} currency  记录自己的币种（ISO 代码）
 * @param {object} [opts]
 * @param {number} [opts.decimals=2]
 * @param {string} [opts.empty='-']   amount 为空时的占位
 * @param {boolean} [opts.negative=false] 前置减号（如折扣 "-¥10.00"）
 */
export const formatMoney = (amount, currency, opts = {}) => {
    const { empty = '-', negative = false, original = false } = opts;
    let { decimals = 2 } = opts;
    if (amount === null || amount === undefined || amount === '') return empty;
    let n = Number(amount);
    if (!Number.isFinite(n)) return String(amount);
    const shown = original ? null : convertForDisplay(n, currency);
    if (shown) {
        n = shown.amount;
        currency = shown.currency;
        // 整数价（decimals 0）换算后通常带小数；按目标币种小数位显示。单价类的 4 位小数保留
        if (decimals < shown.decimals || decimals === 2) decimals = shown.decimals;
    }
    const abs = Math.abs(n).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    const sign = negative || n < 0 ? '-' : '';
    return `${sign}${currencySymbol(currency)}${abs}`;
};

/**
 * 展示货币（客户控制台）：AuthContext 拿到用户后调用 setDisplayCurrency(user.currency)，
 * 汇率由 loadDisplayRates() 从 /api/currencies 取并缓存在 localStorage（首屏同步可用）。
 */
const FX_CACHE_KEY = 'fx_display_rates';
const display = { code: '', rates: null };

const readCache = () => {
    try {
        const c = JSON.parse(localStorage.getItem(FX_CACHE_KEY) || 'null');
        return c && c.enabled && c.rates ? c : null;
    } catch (_) {
        return null;
    }
};

const displayEnabled = () => process.env.REACT_APP_BUILD_MODE !== 'admin'
    && !(typeof window !== 'undefined' && window.location.hostname.startsWith('spectrum.'));

export const setDisplayCurrency = (code) => {
    display.code = displayEnabled() ? normalizeCurrency(code) : '';
    if (display.code && !display.rates) display.rates = readCache()?.rates || null;
};

/** 当前展示货币（未启用折算时为空） */
export const getDisplayCurrency = () => (display.code && display.rates?.[display.code] ? display.code : '');

export const loadDisplayRates = async (fetcher) => {
    if (!displayEnabled()) return;
    try {
        const res = await fetcher();
        const body = res?.data || {};
        const maxAge = Number(body.fx?.max_age_days || 0);
        const rates = {};
        (body.data || []).forEach((c) => {
            const fresh = !maxAge || !c.updated_at || Date.now() - new Date(String(c.updated_at).replace(' ', 'T')).getTime() < maxAge * 86400000;
            if (Number(c.exchange_rate) > 0 && fresh) {
                rates[normalizeCurrency(c.code)] = { rate: Number(c.exchange_rate), decimals: Number(c.decimal_places ?? 2) };
            }
        });
        const enabled = !!body.fx?.enabled;
        display.rates = enabled ? rates : null;
        try { localStorage.setItem(FX_CACHE_KEY, JSON.stringify({ enabled, rates })); } catch (_) { /* ignore */ }
    } catch (_) { /* keep cached rates */ }
};

/** 按展示货币换算；不需要/不能换算时返回 null */
export const convertForDisplay = (amount, currency) => {
    const from = normalizeCurrency(currency);
    const to = display.code;
    if (!to || !from || from === to || !display.rates) return null;
    const f = display.rates[from];
    const t = display.rates[to];
    if (!f || !t) return null;
    return { amount: (Number(amount) / f.rate) * t.rate, currency: to, decimals: Math.max(0, Math.min(4, t.decimals)) };
};

export default formatMoney;
